import React, { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import * as actions from "../../store/actions";
import GridItem from "../Grid/GridItem";
import AsynchronousAutocomplete from "../Autocomplete/AsynchronousAutocomplete";
import TranslationWrapper, { getTranslation } from "../TranslationWrapper/TranslationWrapper";
import { useDialog } from "../../licensing/utils/DialogProvider";
import { Box, Button, Divider } from "@mui/material";
import AdvancedFilter from "./AdvancedFilter";
import PropTypes from "prop-types";

export const getVestigingFilterState = (state, ignoreVenue = false) => {
  const filters = {};
  if (state.filtersV2["vestiging.venue_id"] && !ignoreVenue) {
    filters["vestiging.venue_id"] = state.filtersV2["vestiging.venue_id"];
  }
  if (state.filtersV2["vestiging_id"]) {
    filters["vestiging_id"] = state.filtersV2.vestiging_id;
  }
  return filters;
};

type Filter = {
  field: string;
  operator: string;
  value: any;
  label?: string;
};

type JamezzReduxState = {
  filtersV2: {
    "vestiging.venue_id"?: Filter;
    vestiging_id?: Filter;
  };
};

export const getVestigingFilterStateV2 = (state: JamezzReduxState) => {
  const filters = {};
  if (state.filtersV2["vestiging_id"]) {
    filters["vestiging_id"] = state.filtersV2.vestiging_id;
  }
  return filters;
};

type VestigingRequest = {
  venue_id?: number | string;
};

type VestigingFilterProps = {
  useMenukaartReferences?: boolean;
  extraFilters?: object;
  showAdvancedButton?: boolean;
};

/**
 * Salesarea can have salesarea.menukaartVestiging.
 * This is an id refering to a different salesarea
 * Sometimes, you want to show the referenced salesarea, rather than the referencing one.
 * This is the referenced salesarea as state.
 */
function useDereferenceVestingingOnMenukaart(): [
  VestigingSearchResponse[],
  (value: VestigingSearchResponse[], options: VestigingSearchResponse[]) => void,
] {
  const [reference, setReference] = useState<VestigingSearchResponse[]>([]);

  return [
    reference,
    (value: VestigingSearchResponse[], options: VestigingSearchResponse[]) => {
      const result = value.map((value) => {
        const found = options.find((option) => option.value == value.value);

        const referencedVestiging = found?.menukaartVestiging;
        let result = null;
        if (referencedVestiging && referencedVestiging > 0) {
          result = options.find((option) => option.value === referencedVestiging) ?? null;
        }
        return result;
      });

      setReference(result.filter((a) => a) as VestigingSearchResponse[]); // filter empty values
    },
  ];
}

type VestigingSearchResponse = {
  value: string | number;
  label: string;
  appTarget: string;
  menukaartVestiging: number | null;
};
/**
 * Simplified version of VestigingFilter.
 * Skips use of JamezzMainController in Vestigingen
 * @param useMenukaartReferences If true, will resolve the reference and filter out salesarea with referencing menus
 */
const VestigingFilter = ({ useMenukaartReferences, extraFilters, showAdvancedButton }: VestigingFilterProps) => {
  const venueFilter = useSelector((state: JamezzReduxState) => state.filtersV2["vestiging.venue_id"]);
  const vestigingFilter = useSelector((state: JamezzReduxState) => state.filtersV2.vestiging_id);

  const dispatch = useDispatch();
  const [reference, dereferenceIfHasReference] = useDereferenceVestingingOnMenukaart();

  const setFilter = (key: string, filter: object | null) => {
    return dispatch({
      type: actions.SET_FILTER,
      payload: { key, filter },
    });
  };

  const requestBody: VestigingRequest = {};
  if (venueFilter) {
    requestBody.venue_id = venueFilter.value;
  }

  useEffect(() => {
    if (reference && reference.length > 0) {
      setFilter("vestiging_id", {
        field: "vestiging_id",
        operator: "in",
        value: reference.map((value) => value.value),
        label: reference.map((value) => value.label),
        appTarget: reference.map((value) => value.appTarget),
      });
    }
  }, [reference]);

  const initialValue = useMemo(() => {
    if (vestigingFilter?.value && Array.isArray(vestigingFilter.value)) {
      return vestigingFilter.value.map((value, index) => ({
        value,
        label: vestigingFilter.label?.[index],
        appTarget: vestigingFilter.appTarget?.[index],
        menukaartVestiging: vestigingFilter.menukaartVestiging?.[index] ?? null,
      }));
    } else if (vestigingFilter?.value) {
      return [{ value: vestigingFilter?.value, label: vestigingFilter.label }];
    } else {
      return [];
    }
  }, [vestigingFilter]);

  const url = useMemo(() => {
    let url = "core/vestiging_search?";
    if (extraFilters) {
      Object.keys(extraFilters).forEach((key) => {
        if (extraFilters[key]) {
          url += extraFilters[key].field + "=" + extraFilters[key].value;
        }
      });
    }
    return url;
  }, [extraFilters]);

  return (
    <GridItem sm={4} xs={12}>
      <AsynchronousAutocomplete
        async={{
          url: url,
        }}
        autocompleteProps={{
          ListboxComponent: showAdvancedButton === false ? undefined : ListBoxComponent,
        }}
        filterOptions={(options, state) => {
          let filtered = options.filter((option: VestigingSearchResponse) =>
            option.label.toLowerCase().includes(state.inputValue.toLowerCase())
          );

          if (useMenukaartReferences) {
            filtered = filtered.filter((option: VestigingSearchResponse) => {
              return option.menukaartVestiging === 0 || option.menukaartVestiging == null;
            });
          }
          return filtered;
        }}
        getOptionLabel={(option) => {
          return "#" + option.value + " " + option.label;
        }}
        multiple={true}
        onChange={(e, option) => {
          if (option && option.length > 0) {
            setFilter("vestiging_id", {
              field: "vestiging_id",
              operator: "in",
              value: option.map((o) => o.value),
              label: option.map((o) => o.label),
              appTarget: option.map((o) => o.appTarget),
            });
          } else {
            setFilter("vestiging_id", null);
          }
        }}
        onOptionsChange={(value, options) => {
          if (useMenukaartReferences) {
            dereferenceIfHasReference(value, options);
          }
        }}
        placeholder={getTranslation("global.filters.vestigigen.filter.selectText", "Selecteer een vestiging")}
        preloadOptions={true}
        value={initialValue}
      />
    </GridItem>
  );
};

function ListBoxComponent(props: any) {
  const [openDialog] = useDialog();
  return (
    <Box {...props}>
      {props.children}
      <Divider />

      <Button
        onClick={() => {
          openDialog({ children: <AdvancedFilter /> });
        }}
        style={{ width: "100%" }}
      >
        <TranslationWrapper tDefault={"Advanced Search"} tKey={"Advanced Search"} />
      </Button>
    </Box>
  );
}

VestigingFilter.propTypes = {
  useMenukaartReferences: PropTypes.bool,
};

export default VestigingFilter;
